import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiFetch } from "../api/client.ts";

interface Flag {
  id: string;
  key: string;
  enabled: boolean;
  description: string | null;
}

export default function FeatureFlagsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["flags"], queryFn: () => apiFetch<Flag[]>("/feature-flags") });
  const [form, setForm] = useState({ key: "", description: "", enabled: false });

  const create = useMutation({
    mutationFn: () => apiFetch<Flag>("/feature-flags", { method: "POST", body: JSON.stringify(form) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flags"] });
      setForm({ key: "", description: "", enabled: false });
    },
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiFetch<Flag>(`/feature-flags/${id}`, { method: "PATCH", body: JSON.stringify({ enabled }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flags"] }),
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Feature flags</h1>

      {create.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(create.error as Error).message || "Request failed"}
        </div>
      )}
      {toggle.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {(toggle.error as Error).message || "Toggle failed"}
        </div>
      )}

      <form
        className="card flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <label className="text-sm sm:flex-1 sm:min-w-[10rem]">
          <span className="mb-1 block font-medium">Key</span>
          <input className="input w-full" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })} pattern="[a-z0-9_.:\\-]+" required />
        </label>
        <label className="text-sm sm:flex-[2] sm:min-w-[12rem]">
          <span className="mb-1 block font-medium">Description</span>
          <input className="input w-full" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>
        <label className="flex items-center gap-2 text-sm sm:pb-2">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> enabled
        </label>
        <button className="btn w-full sm:w-auto" type="submit">Create</button>
      </form>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Key</th>
              <th className="hidden px-4 py-2 md:table-cell">Description</th>
              <th className="px-4 py-2">Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data?.map((f) => (
              <tr key={f.id}>
                <td className="break-all px-4 py-2 font-mono">{f.key}</td>
                <td className="hidden px-4 py-2 text-slate-600 md:table-cell">{f.description}</td>
                <td className="px-4 py-2">
                  <input type="checkbox" checked={f.enabled} onChange={(e) => toggle.mutate({ id: f.id, enabled: e.target.checked })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
